import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { 
  getQuizScoreBgColorClass, 
  getQuizScoreTextColorClass,
  getCheatingProbabilityBgColorClass,
  getCheatingProbabilityTextColorClass,
  getCheatingProbabilityLabel
} from '../Utils/ColorUtils';
import TalentInfoHeader from '../Utils/TalentInfoHeader';
import ProfileMetricCard from './Shards/ProfileMetricCard';
import AdditionalMetricCard from './Shards/AdditionalMetricCard';
import StrengthsConcernsCard from './Shards/StrengthsConcernsCard';
import HardSkillsSection from './Shards/HardSkillsSection';
import SoftSkillsSection from './Shards/SoftSkillsSection';
import AreasOfExpertise from './Shards/AreasOfExpertise';
import JobResponsibilitiesFit from './Shards/JobResponsibilitiesFit';
import EvaluationsOverview from './Shards/EvaluationsOverview';
import QuizSummary from './Shards/QuizSummary';
import SpokenLanguageSummary from './Shards/SpokenLanguageSummary';

const PrintableEvaluationReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    evaluationData, 
    candidateProfile, 
    softSkillsData, 
    spokenLanguageData, 
    cheatingData,
    practicalQuizResponse,
    quizDuration,
    roleCategory,
    evals
  } = location.state || {};

  // Parse data if it's a string
  const parsedEvaluationData = typeof evaluationData === 'string' ? JSON.parse(evaluationData) : evaluationData;
  const parsedSoftSkillsData = typeof softSkillsData === 'string' ? JSON.parse(softSkillsData) : softSkillsData;
  const parsedSpokenLanguageData = typeof spokenLanguageData === 'string' ? JSON.parse(spokenLanguageData) : spokenLanguageData;
  const parsedCheatingData = typeof cheatingData === 'string' ? JSON.parse(cheatingData) : cheatingData;

  // Extract data from candidateProfile if available
  const name = candidateProfile ? (candidateProfile.firstname + ' ' + candidateProfile.lastname || 'N/A') : 'N/A';
  const roleCategoryText = roleCategory?.mainCategory || "";
  const role = roleCategory?.subCategory || candidateProfile?.role?.content || "N/A";
  const totalYearsOfExperience = candidateProfile?.totalYearsOfExperience?.years || 'N/A';
  
  // Extract data from the parsed evaluationData if available
  const seniority = parsedEvaluationData?.seniority || {};
  const conclusion = parsedEvaluationData?.conclusion || {};
  const generalAbility = parsedEvaluationData?.generalAbility || {};
  const jobFit = parsedEvaluationData?.jobFit || {};
  const aiProficiency = parsedEvaluationData?.["AI Proficiency"] || {};
  const multidisciplinaryExpertise = parsedEvaluationData?.["Multidisciplinary Expertise"] || {};
  const securityAwareness = parsedEvaluationData?.["Security Awareness"] || {};
  const strengths = parsedEvaluationData?.strengths || {};
  const concerns = parsedEvaluationData?.concerns || {};
  const hardSkills = parsedEvaluationData?.hardSkills || {};
  const expertise = parsedEvaluationData?.expertise || {};
  const responsibilitiesFit = parsedEvaluationData?.responsibilitiesFit || {};

  // Parse the practical quiz response if needed
  const parsedQuizResponse = typeof practicalQuizResponse === 'string' 
    ? JSON.parse(practicalQuizResponse) 
    : practicalQuizResponse;

  return (
    <div className="p-8 max-w-5xl mx-auto bg-white">
      {/* Back button - only visible on screen, not when printing */}
      <button 
        onClick={() => navigate(-1)} 
        className="mb-6 flex items-center text-blue-600 hover:text-blue-800 print:hidden"
      >
        <FaArrowLeft className="mr-2" /> Back to Evaluation
      </button>

      {/* Page title - only visible on screen, not when printing */}
      <h1 className="text-3xl font-bold mb-8 text-center print:hidden">Printable Evaluation Report</h1>
      
      {/* Print instructions - only visible on screen, not when printing */}
      <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg print:hidden">
        <p className="text-blue-800">
          This page is optimized for printing. Use Cmd+P (Mac) or Ctrl+P (Windows) to print or save as PDF.
          The header and this message will not appear in the printed version.
        </p>
      </div>

      {/* Report title - visible in print */}
      <h1 className="text-3xl font-bold mb-6 text-center hidden print:block">Candidate Evaluation Report</h1>
      
      {/* Header with candidate info */}
      <TalentInfoHeader
        name={name}
        roleCategory={roleCategoryText}
        role={role}
        yearsOfExperience={totalYearsOfExperience}
        seniority={seniority}
        showRationale={true}
      />
      
      {/* Conclusion Section */}
      <div className="print-break-inside-avoid">
        <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 relative">
          <div className="flex justify-between items-start mb-3">
            <h4 className="text-lg font-semibold text-gray-800">Conclusion</h4>
            {conclusion?.source && (
              <span className="text-xs text-gray-500">Source: {conclusion.source}</span>
            )}
          </div>
          <div className="h-full">
            <p className="text-gray-800 text-lg">{conclusion?.value || "No conclusion available."}</p>
          </div>
        </div>
      </div>
      
      {/* General Ability and Job Fit Section */}
      <div className="mt-6 print-break-inside-avoid">
        {/* General Ability Chart */}
        <ProfileMetricCard
          title="General Ability"
          value={generalAbility?.value || 0}
          rationale={generalAbility?.rationale}
          source={generalAbility?.source}
        />
        
        {/* Job Fit Chart */}
        <div className="mt-4">
          <ProfileMetricCard
            title="Job Fit"
            value={jobFit?.value || 0}
            rationale={jobFit?.rationale}
            source={jobFit?.source}
          />
        </div>
      </div>
      
      {/* Practical Quiz Summary Section */}
      {parsedQuizResponse && (
        <div className="mt-6 print-break-inside-avoid">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <QuizSummary 
              practicalQuizResponse={parsedQuizResponse}
              quizDuration={quizDuration}
              cheatingData={parsedCheatingData}
              layout="rows"
            />
          </div>
        </div>
      )}
      
      {/* Additional Metrics Section */}
      <div className="mt-6 print-break-inside-avoid">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">Additional Key Metrics</h4>
          <div className="space-y-4">
            {/* AI Proficiency */}
            <AdditionalMetricCard 
              title="AI Proficiency"
              value={aiProficiency?.value || 0}
              rationale={aiProficiency?.rationale}
              source={aiProficiency?.source}
            />
            
            {/* Multidisciplinary Expertise */}
            <AdditionalMetricCard 
              title="Multidisciplinary Expertise"
              value={multidisciplinaryExpertise?.value || 0}
              rationale={multidisciplinaryExpertise?.rationale}
              source={multidisciplinaryExpertise?.source}
            />
            
            {/* Security Awareness */}
            <AdditionalMetricCard 
              title="Security Awareness"
              value={securityAwareness?.value || 0}
              rationale={securityAwareness?.rationale}
              source={securityAwareness?.source}
            />
          </div>
        </div>
      </div>
      
      {/* Evaluations Overview Section */}
      <div className="mt-6 print-break-inside-avoid">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <EvaluationsOverview 
            evals={evals || []}
          />
        </div>
      </div>
      
      {/* Strengths and Concerns Section */}
      <div className="mt-6 print-break-inside-avoid">
        <StrengthsConcernsCard 
          strengths={strengths}
          concerns={concerns}
        />
      </div>
      
      {/* Hard Skills Section */}
      <div className="mt-6 print-break-inside-avoid">
        <HardSkillsSection hardSkills={hardSkills} radarChartMode="all" />
      </div>
      
      {/* Soft Skills Section */}
      {parsedSoftSkillsData && (
        <div className="mt-6 print-break-inside-avoid">
          <SoftSkillsSection softSkills={{ value: parsedSoftSkillsData, source: "Resume, Vetting" }} />
        </div>
      )}
      
      {/* Spoken Language Section */}
      {parsedSpokenLanguageData && parsedSpokenLanguageData.length > 0 && (
        <div className="mt-6 print-break-inside-avoid">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <SpokenLanguageSummary spokenLanguageData={parsedSpokenLanguageData} />
          </div>
        </div>
      )}
      
      {/* Areas of Expertise Section */}
      <div className="mt-6 print-break-inside-avoid">
        <AreasOfExpertise expertise={expertise} />
      </div>
      
      {/* Job Responsibilities Fit Section */}
      <div className="mt-6 print-break-inside-avoid">
        <JobResponsibilitiesFit responsibilitiesFit={responsibilitiesFit} />
      </div>
      
      {/* Footer with page number - only visible when printing */}
      <div className="mt-8 text-center text-gray-500 text-sm hidden print:block">
        <p>Generated on {new Date().toLocaleDateString()}</p>
      </div>
      
      {/* Add print-specific CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
      @page {
        size: A4;
        margin: 0.8cm;
      }
            body {
              font-size: 12pt;
              color: #000;
              background-color: #fff;
            }
            .print-break-inside-avoid {
              break-inside: avoid;
            }
            .print-break-before {
              break-before: page;
            }
            /* Force flex-row layout in print view for quiz score and cheating probability sections */
            .flex-col.md\\:flex-row {
              flex-direction: row !important;
            }
            /* Ensure left column maintains width in print view */
            .md\\:w-1\\/3 {
              width: 33.333333% !important;
              min-width: 200px !important;
              flex-shrink: 0 !important;
            }
            /* Ensure right column takes remaining width in print view */
            .md\\:w-2\\/3 {
              width: 66.666667% !important;
              padding-left: 20px !important;
            }
            /* Make radar charts bigger in print view */
            .w-full.h-96 {
              
            }
            /* Force single column layout for evaluations overview in print */
            .grid.grid-cols-1.md\\:grid-cols-2 {
              grid-template-columns: 1fr !important;
            }
            /* Ensure evaluation overview cards stack vertically in print */
            .grid.grid-cols-1.md\\:grid-cols-2 > div {
              margin-bottom: 1rem !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default PrintableEvaluationReport;
